/**
 * A repo moves to another project (RFC 0357 3i, S14).
 *
 * The source record goes with its git pointer, under the same id unless the
 * destination already has one by that name. The move is recorded on the World
 * before the old record goes, as making a folder a project does, so every
 * device's daemon rehomes its own mount (`pendingSourceMountRehomes`), even one
 * that was offline when it happened.
 *
 * Sessions working in the repo go with it: a session left behind would ask its
 * old project for a source that is no longer there and fail its next turn.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { projectSessionCode } from '../code/reads'
import { ensureProjectSources, projectSources } from '../code/sources'
import { normalizeDocumentPath } from '../documents/mutations'
import { changeProject, projectString } from '../project/scalars'
import type { ProjectDoc, ProjectSource } from '../project/types'
import { moveSession } from '../session/mutations'
import type { SessionDoc } from '../session/types'
import { recordSourceMove } from './mutations'
import { worldProjectMounts } from './reads'
import type { WorldDoc } from './types'

export async function moveSourceToProject(
  repo: Repo,
  worldId: string,
  fromProjectId: string,
  sourceId: string,
  toProjectId: string,
  folder: string,
  /** A name for a source that only had the placeholder, from the folder
   *  this device mounts it at. Names are shared, so the caller supplies it. */
  name?: string,
): Promise<{ sourceId: string; sessions: string[] }> {
  if (fromProjectId === toProjectId)
    throw new Error('the repo is already in that project')
  const path = normalizeDocumentPath(folder)
  if (!path) throw new Error(`"${folder}" is not a folder path`)
  const ready = async <T>(id: string) => {
    const h = await repo.find<T>(id as AnyDocumentId)
    await (h as { whenReady?: () => Promise<void> }).whenReady?.()
    return h
  }
  const from = await ready<ProjectDoc>(fromProjectId)
  const to = await ready<ProjectDoc>(toProjectId)
  const world = await ready<WorldDoc>(worldId)

  const source = projectSources(from.doc()).sources.find(
    (s) => s.id === sourceId,
  )
  if (!source) throw new Error('unknown source')

  // Where it lands must be free: no document, other repo or nested project.
  for (const ref of Object.values(to.doc()?.documents ?? {})) {
    const at = normalizeDocumentPath(String(ref.path))
    if (at === path || at?.startsWith(`${path}/`) || path.startsWith(`${at}/`))
      throw new Error(`${path} already holds a document there`)
  }
  for (const other of projectSources(to.doc()).sources) {
    if (!other.folder) continue
    if (
      other.folder === path ||
      other.folder.startsWith(`${path}/`) ||
      path.startsWith(`${other.folder}/`)
    )
      throw new Error(`${path} overlaps source ${other.name} there`)
  }
  if (
    worldProjectMounts(world.doc()).some(
      (mount) =>
        mount.parentProjectId === toProjectId &&
        (mount.folder === path ||
          mount.folder.startsWith(`${path}/`) ||
          path.startsWith(`${mount.folder}/`)),
    )
  )
    throw new Error(`${path} overlaps a nested project there`)

  // The same id unless the destination has it, as an older project's assumed
  // `default` would.
  const taken = new Set(projectSources(to.doc()).sources.map((s) => s.id))
  const toSourceId = taken.has(sourceId)
    ? `${sourceId}-${crypto.randomUUID().slice(0, 8)}`
    : sourceId
  const moved: ProjectSource = {
    ...JSON.parse(JSON.stringify(source)),
    id: toSourceId,
    folder: path,
    // A moved guess becomes a real source: someone chose to put it here.
    ...(name ? { name } : {}),
  }
  delete moved.assumed

  // Every session the old project lists that runs in this repo, read before
  // anything is written. One whose document cannot be opened here stays: its
  // source is unknown, and moving it on a guess is worse than leaving it.
  const sessions: { id: string; field: 'codeSourceId' | 'code' }[] = []
  for (const raw of from.doc()?.sessionIds ?? []) {
    const id = projectString(raw)
    if (!id) continue
    let doc: SessionDoc | undefined
    try {
      doc = (await ready<SessionDoc>(id)).doc()
    } catch {
      continue
    }
    const own = projectString(doc?.codeSourceId)
    if (own === sourceId) sessions.push({ id, field: 'codeSourceId' })
    else if (!own && projectSessionCode(doc)?.sourceId === sourceId)
      sessions.push({ id, field: 'code' })
  }

  // 1. Listed in the new project. Materialising first keeps that project's
  //    own assumed source, which an explicit map would otherwise replace.
  changeProject(to, (d: ProjectDoc) => {
    ensureProjectSources(d)
    if (!d.sources) d.sources = {}
    d.sources[toSourceId] = moved
    if (!d.defaultSourceId) d.defaultSourceId = toSourceId
  })
  // 2. Recorded, so each device's mount follows before the old record goes.
  world.change((d: WorldDoc) => {
    recordSourceMove(d, fromProjectId, sourceId, toProjectId, toSourceId)
  })
  // 3. The sessions follow, each listed there before it leaves here
  //    (`moveSession`), and pointed at the new id if the source took one.
  for (const { id, field } of sessions) {
    await moveSession(repo, id, fromProjectId, toProjectId)
    if (toSourceId === sourceId) continue
    const session = await ready<SessionDoc>(id)
    session.change((d) => {
      if (field === 'codeSourceId') d.codeSourceId = toSourceId
      else if (d.code) d.code.sourceId = toSourceId
    })
  }
  // 4. Unlisted from the old project, with its git pointer if it was the one.
  changeProject(from, (d: ProjectDoc) => {
    ensureProjectSources(d)
    if (d.sources) delete d.sources[sourceId]
    if (projectString(d.defaultSourceId) === sourceId) {
      const remaining = Object.keys(d.sources ?? {})
      if (remaining[0]) d.defaultSourceId = remaining[0]
      else delete d.defaultSourceId
    }
    const vcs = d.capabilities?.vcs
    if (
      vcs &&
      source.vcsDocId &&
      projectString(vcs.docId) === source.vcsDocId &&
      d.capabilities
    )
      delete d.capabilities.vcs
  })
  return { sourceId: toSourceId, sessions: sessions.map((s) => s.id) }
}
